import apiClient from './api';

/**
 * Get all Amazon listings with optional filters
 * @param {Object} params - Query parameters for filtering, pagination, etc.
 * @returns {Promise} - Promise with the response
 */
export const getAmazonListings = (params = {}) => {
  // Ensure we always have a page_size parameter
  const finalParams = {
    page_size: 20,  // Default page size
    ...params       // But allow override from caller
  };
  console.log('Fetching Amazon listings with params:', finalParams);
  return apiClient.get('/marketplace/amazon-listings/', { params: finalParams });
};

/**
 * Get a single Amazon listing by ID
 * @param {number} id - The ID of the Amazon listing
 * @returns {Promise} - Promise with the response
 */
export const getAmazonListing = (id) => {
  return apiClient.get(`/marketplace/amazon-listings/${id}/`);
};

/**
 * Upload listings from CSV/Excel file
 * @param {FormData} formData - Form data containing the file and marketplace type
 * @returns {Promise} - Promise with the response
 */
export const uploadMarketplaceFile = (formData) => {
  return apiClient.post('/marketplace/upload-listings/', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};

/**
 * Download a template for marketplace listings
 * @param {string} marketplace - Type of marketplace (amazon, flipkart, myntra, ajio)
 * @param {string} fmt - Format of the template (csv, xlsx)
 * @returns {Promise} - Promise with the response
 */
export const downloadMarketplaceTemplate = (marketplace, fmt = 'xlsx') => {
  return apiClient.get('/marketplace/download-template/', {
    params: { marketplace, fmt },
    responseType: 'blob'
  });
};

/**
 * Get all companies
 * @returns {Promise} - Promise with the response
 */
export const getCompanies = () => {
  return apiClient.get('/marketplace/companies/');
};

/**
 * Get all Flipkart listings with optional filters
 * @param {Object} params - Query parameters for filtering, pagination, etc.
 * @returns {Promise} - Promise with the response
 */
export const getFlipkartListings = (params = {}) => {
  const finalParams = {
    page_size: 20,  // Default page size
    ...params       // But allow override from caller
  };
  console.log('Fetching Flipkart listings with params:', finalParams);
  return apiClient.get('/marketplace/flipkart-listings/', { params: finalParams });
};

/**
 * Get all Myntra listings with optional filters
 * @param {Object} params - Query parameters for filtering, pagination, etc.
 * @returns {Promise} - Promise with the response
 */
export const getMyntraListings = (params = {}) => {
  const finalParams = {
    page_size: 20,  // Default page size
    ...params       // But allow override from caller
  };
  console.log('Fetching Myntra listings with params:', finalParams);
  return apiClient.get('/marketplace/myntra-listings/', { params: finalParams });
};

/**
 * Get all Ajio listings with optional filters
 * @param {Object} params - Query parameters for filtering, pagination, etc.
 * @returns {Promise} - Promise with the response
 */
export const getAjioListings = (params = {}) => {
  const finalParams = {
    page_size: 20,  // Default page size
    ...params       // But allow override from caller
  };
  console.log('Fetching Ajio listings with params:', finalParams);
  return apiClient.get('/marketplace/ajio-listings/', { params: finalParams });
};

/**
 * Get all Nykaa listings with optional filters
 * @param {Object} params - Query parameters for filtering, pagination, etc.
 * @returns {Promise} - Promise with the response
 */
export const getNykaaListings = (params = {}) => {
  const finalParams = {
    page_size: 20,
    ...params
  };
  console.log('Fetching Nykaa listings with params:', finalParams);
  return apiClient.get('/marketplace/nykaa-listings/', { params: finalParams });
};

/**
 * Get all marketplace listings for a specific SKU
 * @param {string} skuCode - The SKU code to get listings for
 * @returns {Promise} - Promise with the response containing all marketplace listings
 */
export const getListingsBySku = (skuCode) => {
  return apiClient.get('/marketplace/listings-by-sku/', { 
    params: { sku: skuCode } 
  });
};

/**
 * Download Amazon listings for a specific company
 * @param {string} companyName - The name of the company to filter listings by
 * @returns {Promise} - Promise with the file download response
 */
export const downloadAmazonListings = (companyName) => {
  return apiClient.get('/marketplace/amazon-listings/listing_download/', { 
    params: { company_name: companyName },
    responseType: 'blob' // Important for file downloads
  });
};

/**
 * Download Myntra listings for a specific company
 * @param {string} companyName - The name of the company to filter listings by
 * @returns {Promise} - Promise with the file download response
 */
export const downloadMyntraListings = (companyName) => {
  return apiClient.get('/marketplace/myntra-listings/listing_download/', { 
    params: { company_name: companyName },
    responseType: 'blob' // Important for file downloads
  });
};

/**
 * Download Ajio listings for a specific company
 * @param {string} companyName - The name of the company to filter listings by
 * @returns {Promise} - Promise with the file download response
 */
export const downloadAjioListings = (companyName) => {
  return apiClient.get('/marketplace/ajio-listings/listing_download/', { 
    params: { company_name: companyName },
    responseType: 'blob' // Important for file downloads
  });
};

/**
 * Download Nykaa listings for a specific company
 * @param {string} companyName - The name of the company to filter listings by
 * @returns {Promise} - Promise with the file download response
 */
export const downloadNykaaListings = (companyName) => {
  return apiClient.get('/marketplace/nykaa-listings/listing_download/', {
    params: { company_name: companyName },
    responseType: 'blob'
  });
};

/**
 * Download Flipkart listings for a specific company
 * @param {string} companyName - The name of the company to filter listings by
 * @returns {Promise} - Promise with the file download response
 */
export const downloadFlipkartListings = (companyName) => {
  return apiClient.get('/marketplace/flipkart-listings/listing_download/', {
    params: { company_name: companyName },
    responseType: 'blob'
  });
};

/**
 * Download Flipkart listings by uploading an Excel file containing Flipkart Serial Numbers
 * @param {FormData} formData - Form data containing the Excel file 
 * @returns {Promise} - Promise with the CSV file response
 */
export const downloadFlipkartListingsByExcel = (formData) => {
  return apiClient.post('/marketplace/flipkart-listings/listing_download/', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    responseType: 'blob' // Important for file downloads
  });
};

/**
 * Download Ajio listings by uploading an Excel file containing JIO codes
 * @param {FormData} formData - Form data containing the Excel file 
 * @returns {Promise} - Promise with the CSV file response
 */
export const downloadAjioListingsByExcel = (formData) => {
  return apiClient.post('/marketplace/ajio-listings/listing_download/', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    responseType: 'blob' // Important for file downloads
  });
};

/**
 * Get all Return Entry Logs with optional filters
 * @param {Object} params - Query parameters for filtering, pagination, etc.
 * @returns {Promise} - Promise with the response
 */
export const getReturnEntryLogs = (params = {}) => {
  const finalParams = {
    page_size: 20,  // Default page size
    ...params       // But allow override from caller
  };
  console.log('Fetching Return Entry Logs with params:', finalParams);
  return apiClient.get('/marketplace/return-entries-log/', { params: finalParams });
};

/**
 * Create a new Return Entry Log
 * @param {Object} data - The data for the new return entry log
 * @returns {Promise} - Promise with the response
 */
export const createReturnEntryLog = (data) => {
  return apiClient.post('/marketplace/return-entries-log/', data);
};

/**
 * Delete a Return Entry Log
 * @param {number} id - The ID of the return entry log to delete
 * @returns {Promise} - Promise with the response
 */
export const deleteReturnEntryLog = (id) => {
  return apiClient.delete(`/marketplace/return-entries-log/${id}/`);
};

/**
 * Download Excel file of all Return Entry Logs
 * @returns {Promise} - Promise with the response containing Excel file
 */
export const downloadReturnEntryLogsExcel = () => {
  return apiClient.get('/marketplace/return-entries-log/download-excel/', {
    responseType: 'blob'
  });
};

/**
 * Upload Excel file to bulk create/update Return Entry Logs
 * @param {FormData} formData - Form data containing the Excel file
 * @returns {Promise} - Promise with the response
 */
export const uploadReturnEntryLogsExcel = (formData) => {
  return apiClient.post('/marketplace/return-entries-log/upload-excel/', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};

/**
 * Check AWB availability by uploading Excel file
 * @param {FormData} formData - Form data containing the Excel file with AWB numbers
 * @returns {Promise} - Promise with the response containing modified Excel file
 */
export const checkAwbAvailability = (formData) => {
  return apiClient.post('/marketplace/return-entries-log/check-awb-availability/', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    responseType: 'blob' // Important for file downloads
  });
};
